/**
 * Storefront Core — the URL a language switcher links to.
 *
 * Pure (no React, no window): the header components pass in what they read from the router / page-locale context.
 * Moved verbatim from the legacy header so every theme's language switcher behaves identically:
 *   - a CMS page (`/pages/<slug>`) has a genuinely different slug per language, so the slug is translated through the
 *     current page's own PageTranslation relationship, never by reusing the source-language slug;
 *   - with no known translation the target language's homepage is used rather than a guessed slug;
 *   - every other route keeps its path and swaps (or adds) the locale segment.
 */

export type LanguageSwitchContext = {
  /** Current pathname, e.g. "/tr/products/x". */
  pathname: string | null | undefined;
  /** Current query string WITHOUT the leading "?". */
  query: string;
  /** Current hash INCLUDING "#" (or ""). */
  hash: string;
  /** The page-locale registration of the CURRENT CMS page (PageLocaleContext), if any. */
  pageLocale?: { sourceSlug: string; slugs: Record<string, string> } | null;
};

export function buildLanguageHref(language: string, context: LanguageSwitchContext): string {
  const normalizedLanguage = language.trim().toLowerCase();
  const currentPath = context.pathname || "/";
  const segments = currentPath.split("/").filter(Boolean);
  const hasLocalePrefix = segments[0] === "en" || segments[0] === "tr";
  const pathSegments = hasLocalePrefix ? segments.slice(1) : segments;
  // CMS page links always carry an explicit locale segment (matching the site's own
  // `/en/pages/...` / `/tr/pages/...` URL scheme) — unlike the non-CMS branch below,
  // which mirrors this same page's existing (unconditional) locale-prefix behavior.
  const langPrefix = `/${normalizedLanguage}`;

  const withQueryAndHash = (path: string) => {
    const withQuery = context.query ? `${path}?${context.query}` : path;
    return `${withQuery}${context.hash}`;
  };

  if (pathSegments[0] === "pages" && pathSegments.length > 1) {
    const currentSlugSegment = pathSegments[pathSegments.length - 1];
    const isFreshForThisPage = context.pageLocale?.sourceSlug === currentSlugSegment;
    const targetSlug = isFreshForThisPage ? context.pageLocale!.slugs[normalizedLanguage] : undefined;

    if (targetSlug) {
      return withQueryAndHash(`${langPrefix}/pages/${targetSlug}`);
    }

    // No known translation for this page in the target language: fall back to the
    // target language's homepage rather than emit a guessed/wrong slug.
    return withQueryAndHash(langPrefix);
  }

  if (hasLocalePrefix) {
    segments[0] = normalizedLanguage;
  } else {
    segments.unshift(normalizedLanguage);
  }

  const nextPath = `/${segments.join("/")}`.replace(/\/$/, "") || `/${normalizedLanguage}`;
  return withQueryAndHash(nextPath);
}
